'use client';

import type { Url } from 'next/dist/shared/lib/router/router';
import { DOTS } from '@/constants/paginationConstants';
import type { Locale } from '@/i18n';
import { formatMessage } from '@/utils/translationHelper';
import useAnalytics from '@/hooks/useAnalytics';
import { FOOTER } from '@/utils/analyticsClientHelper';
import type { SimpleClickFact } from '@/analytics/models/cventAnalytics/SimpleClickFact';
import { getSimpleClickFact } from '@/analytics/helpers/cventAnalytics/getSimpleClickFact';
import { type ReactNode, useCallback } from 'react';
import useMap from '@/hooks/useMap';
import ItemElement from './components/ItemElement';

interface Props {
  children: ReactNode;
  dataCventId: string;
  paginationAriaLabel: string;
}

interface PaginationProps {
  /** The currently selected page index. */
  selected: number;
  searchTerm: string;
  /** Returns the url for the page */
  onPress: (page: number) => Url;
  /** The range of pages to display. */
  paginationRange: (string | number)[];
  strings: {
    pagination_aria_label: string;
    pagination_previous_icon_aria_label: string;
    pagination_next_icon_aria_label: string;
    page_number_aria_label: string;
  };
  locale: Locale;
}

function PageNavigation({ children, dataCventId, paginationAriaLabel }: Props) {
  return (
    <nav
      aria-label={paginationAriaLabel}
      className="flex h-7 shrink-0 items-center justify-between self-center pb-2"
      data-cvent-id={dataCventId}
      role="navigation"
    >
      {children}
    </nav>
  );
}

// TODO: add translated aria-labels after content is finalized
export default function Pagination({
  selected,
  searchTerm,
  onPress,
  paginationRange,
  strings,
  locale
}: PaginationProps) {
  const analytics = useAnalytics();
  const { mapView } = useMap();
  const lastPage = paginationRange[paginationRange.length - 1];

  const triggerSimpleClickFact = useCallback(
    (clickEntity: string, prevPage: number) => {
      const metadata = JSON.stringify({ prevPage });
      const simpleClickFact: SimpleClickFact = getSimpleClickFact(
        clickEntity,
        FOOTER,
        'null',
        'null',
        mapView,
        searchTerm,
        'null',
        'null',
        'null',
        'null',
        'null',
        metadata
      );
      analytics.reportFact<SimpleClickFact>(simpleClickFact);
    },
    [analytics, mapView, searchTerm]
  );

  const triggerAnalyticsForPagination = useCallback(
    (nextPageNumber: number, currentPageNumber: number) => {
      const clickEntity = `pagination ${nextPageNumber}`;
      triggerSimpleClickFact(clickEntity, currentPageNumber);
    },
    [triggerSimpleClickFact]
  );

  return (
    <PageNavigation dataCventId="pagination-builder" paginationAriaLabel={strings.pagination_aria_label}>
      <>
        <ItemElement
          ariaLabel={strings.pagination_previous_icon_aria_label}
          accessible
          icon="left"
          disabled={selected === 1}
          pageUrl={onPress(selected - 1)}
          dataCventId="pagination-previous"
          onClick={() => triggerAnalyticsForPagination(selected - 1, selected)}
        />
        {paginationRange.map((pageNumber: number, index: number) => {
          if (pageNumber.toString() === DOTS) {
            return (
              <ItemElement
                disabled
                accessible
                text={DOTS}
                /* eslint-disable-next-line react/no-array-index-key */
                key={`${DOTS}-${index}`}
                pageUrl="#"
                dataCventId={`pagination-${index + 1}`}
              />
            );
          }
          return (
            <ItemElement
              key={pageNumber}
              accessible
              ariaLabel={formatMessage(strings.page_number_aria_label, locale, { pageNumber })}
              selected={pageNumber === selected}
              disabled={pageNumber === selected}
              text={pageNumber.toString()}
              pageUrl={onPress(pageNumber)}
              dataCventId={`pagination-${pageNumber}`}
              onClick={pageNumber !== selected ? () => triggerAnalyticsForPagination(pageNumber, selected) : undefined}
            />
          );
        })}
        <ItemElement
          ariaLabel={strings.pagination_next_icon_aria_label}
          accessible
          icon="right"
          disabled={selected === lastPage}
          pageUrl={onPress(selected + 1)}
          dataCventId="pagination-next"
          onClick={() => triggerAnalyticsForPagination(selected + 1, selected)}
        />
      </>
    </PageNavigation>
  );
}
